import {
    Box,
    Button,
    Card,
    Container,
    TextField,
    Typography,
    Alert,
    Link
  } from "@mui/material";
  import { Helmet } from "react-helmet-async";
  import { useState, useEffect } from "react";
  import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
  import { fetchUtils } from "react-admin";
  import * as Yup from "yup";
  import { Formik } from "formik";
  import { BASE_API_URL } from "config/configUrl";
  import { useTranslation } from "react-i18next";
  import LogoCoinhouse from "../../../components/Logo/LogoCoinhouse";
  import { brand } from "../../../themes/brandTokens";
  
  const ResetPasswordToken = () => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const { t }: { t: any } = useTranslation();
  
    const [loading, setLoading] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [showForgotLink, setShowForgotLink] = useState(false);
  
    useEffect(() => {
      if (!token) return;
      const url = new URL(`${BASE_API_URL}/users/password/reset/validate/${token}`);
      fetchUtils
        .fetchJson(url.toString())
        .then(() => {
          setTokenValid(true);
          setLoading(false);
        })
        .catch((err) => {
          setTokenValid(false);
          if (err.status === 410) {
            setError(t("This link has expired. Request a new one."));
            setShowForgotLink(true);
          } else {
            setError(t("This link is invalid or has expired."));
          }
          setLoading(false);
        });
    }, [token]);
  
    const handlePasswordReset = async (
      values: { newPassword: string; confirmPassword: string },
      {
        setSubmitting,
        setStatus,
        setErrors,
      }: {
        setSubmitting: (submitting: boolean) => void;
        setStatus: (status: any) => void;
        setErrors: (errors: any) => void;
      }
    ) => {
      try {
        const url = new URL(`${BASE_API_URL}/users/password/reset/confirm`);
        await fetchUtils.fetchJson(url.toString(), {
          method: "POST",
          body: JSON.stringify({
            token,
            newPassword: values.newPassword,
          }),
        });
  
        setSuccess(true);
        setStatus({ success: true });
        setSubmitting(false);
        setTimeout(() => navigate("/login"), 2000);
      } catch (err: any) {
        setStatus({ success: false });
        setSubmitting(false);
        const errorMsg = err?.body?.message || t("Unexpected error");
        setError(errorMsg);
        setErrors({ newPassword: errorMsg });
      }
    };
  
    return (
      <>
        <Helmet>
          <title>Coinhouse Solutions - Reset Password</title>
        </Helmet>
  
        <Container
          maxWidth="sm"
          sx={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Card sx={{ p: { xs: 3, sm: 5 }, my: 4, width: "100%", maxWidth: 480 }}>
            <Box textAlign="center">
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <LogoCoinhouse fillColor={brand.ink} />
              </Box>
              <Typography variant="h2" sx={{ mb: 1 }}>
                {t("Choose a new password")}
              </Typography>
              <Typography variant="h4" color="text.secondary" fontWeight="normal" sx={{ mb: 3 }}>
                {t("Use at least 8 characters.")}
              </Typography>
            </Box>

            {loading && (
              <Typography color="text.secondary" textAlign="center">
                {t("Checking your link…")}
              </Typography>
            )}
  
            {!loading && !tokenValid && (
              <>
                <Alert severity="error">{error}</Alert>
  
                {showForgotLink && (
                  <Button
                    fullWidth
                    size="large"
                    variant="contained"
                    component={RouterLink}
                    to="/recover-password"
                    sx={{ mt: 3 }}
                  >
                    {t("Request a new link")}
                  </Button>
                )}
              </>
            )}

            {!loading && tokenValid && (
              <>
                <Formik
                  initialValues={{ newPassword: "", confirmPassword: "" }}
                  validationSchema={Yup.object().shape({
                    newPassword: Yup.string()
                      .min(8, t("Use at least 8 characters"))
                      .required(t("Required field")),
                    confirmPassword: Yup.string()
                      .oneOf([Yup.ref("newPassword"), null], t("Passwords must match"))
                      .required(t("Required field")),
                  })}
                  onSubmit={(values, helpers) => handlePasswordReset(values, helpers)}
                >
                  {({
                    errors,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    touched,
                    values,
                    isSubmitting,
                  }) => (
                    <form noValidate onSubmit={handleSubmit}>
                      {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {error}
                        </Alert>
                      )}
  
                      <TextField
                        fullWidth
                        margin="normal"
                        name="newPassword"
                        type="password"
                        label={t("New password")}
                        variant="outlined"
                        value={values.newPassword}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.newPassword && errors.newPassword)}
                        helperText={touched.newPassword && errors.newPassword}
                      />
  
                      <TextField
                        fullWidth
                        margin="normal"
                        name="confirmPassword"
                        type="password"
                        label={t("Confirm new password")}
                        variant="outlined"
                        value={values.confirmPassword}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                        helperText={touched.confirmPassword && errors.confirmPassword}
                      />
  
                      <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        size="large"
                        sx={{ mt: 3 }}
                        disabled={isSubmitting}
                      >
                        {t("Save new password")}
                      </Button>
                    </form>
                  )}
                </Formik>
              </>
            )}
  
            {success && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {t("Password updated. Redirecting to sign in…")}
              </Alert>
            )}

            <Box mt={3} textAlign="center">
              <Link component={RouterLink} to="/login" fontWeight={600}>
                {t("Back to sign in")}
              </Link>
            </Box>
          </Card>
        </Container>
      </>
    );
  };
  
  export default ResetPasswordToken;
  